<template>
  <div>
    <h2>{{ $t('funding.list.mainTitle') }}</h2>
    <div v-if="loadingShow" class="d-flex justify-content-center mb-3">
      <span><b-spinner small class="loading-spinner mr-2"></b-spinner>Data is loading...</span>
    </div>

    <b-alert v-model="hasError" variant="danger">
      {{ errorMessage }}
    </b-alert>

    <dx-data-grid
      v-show="dataIsLoaded"
      :ref="dataGridFundings"
      id="gridContainerFundings"
      :data-source="dataSource"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :column-min-width="50"
      columnResizingMode="widget"
      :show-borders="true"
      :showColumnLines="true"
      :showRowLines="true"
      key-expr="field_001"
      :no-data-text="$t('general.noEntries')"
      :column-auto-width="false"
      :word-wrap-enabled="true"
      :hover-state-enabled="true"
      @row-expanding="openRowExpanding"
      @contentReady="contentReady"
      @row-updated="onUpdateRow"
      @editing-start="onEditingStart"
      :remote-operations="true"
      :sync-lookup-filter-values="false"
    >
      <dx-scrolling
        mode="virtual"
        row-rendering-mode="virtual"
      />
      <dx-paging :page-size="pageSize"/>
      <dx-filter-panel :visible="true"/>
      <dx-filter-row
        :visible="true"
        apply-filter=""
      />
      <dx-header-filter
        :visible="true"
      />
      <dx-column-chooser
        :enabled="true"
        mode="select"
      >
        <dx-column-chooser-search
          :enabled="true"
        />
      </dx-column-chooser>
      <dx-search-panel
        :visible="true"
        :width="320"
        placeholder="Search..."
      />
      <dx-editing
        refresh-mode="repaint"
        :allow-updating="true"
        mode="cell"
      />
      <dx-column
        data-field="profile.cRM_Adress_id"
        data-type="number"
        :caption="$t('funding.list.id')"
        :allow-editing="false"
        :allow-hiding="false"
        :fixed="true"
        :width="100"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="field_001"
        data-type="number"
        :caption="$t('funding.list.fundingId')"
        :allow-editing="false"
        :width="85"
        :allow-filtering="true"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="closingdate"
        :caption="$t('funding.list.closingdate')"
        data-type="date"
        format="dd.MM.yyyy"
        :allow-editing="true"
        :editor-options="{ showClearButton: true }"
        :width="120"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="profile.cRMAddress.company"
        :caption="$t('funding.list.company')"
        :editor-options="{ showClearButton: true }"
        :width="200"
        :allow-header-filtering="false"
        :allow-editing="false"
      />
      <dx-column
        data-field="profile.cRMAddress.firstname"
        :caption="$t('funding.list.firstname')"
        :width="150"
        :visible="false"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="profile.cRMAddress.lastname"
        :caption="$t('funding.list.lastname')"
        :width="150"
        :visible="false"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="profile.cRMAddress.salutationId"
        :caption="$t('funding.list.salutationId')"
        :width="150"
        :visible="false"
        :allow-filtering="false"
      >
        <dx-lookup
          :data-source='salutationTypes'
          :display-expr='getDisplayExpr'
          value-expr='ansprechzeile_id'
        />
      </dx-column>
      <dx-column
        data-field="profile.cRMAddress.emailB1"
        :caption="$t('funding.list.emailB1')"
        :width="150"
        :visible="false"
        :allow-header-filtering="false"
      >
        <dx-email-rule/>
      </dx-column>
      <dx-column
        data-field="profile.cRMAddress.emailB2"
        :caption="$t('funding.list.emailB2')"
        :visible="false"
        :width="150"
        :allow-header-filtering="false"
      >
        <dx-email-rule/>
      </dx-column>
      <dx-column
        data-field="profile.cRMAddress.linkedInB"
        :caption="$t('funding.list.linkedInB')"
        :visible="false"
        :width="150"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="profile.cRMAddress.linkedInP"
        :caption="$t('funding.list.linkedInP')"
        :visible="false"
        :width="150"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="profile.cRMAddress.kantonId"
        :caption="$t('funding.list.canton')"
        :editor-options="{ showClearButton: true }"
        :width="150"
      >
        <dx-lookup
          :data-source="cantonTypes"
          display-expr="kantonEN"
          value-expr="field_001"
        />
      </dx-column>
      <dx-column
        data-field="moneyinCHF"
        data-type="number"
        :caption="$t('funding.list.roundSize')"
        :format="totalFormat"
        :editor-options="{ showClearButton: true }"
        :width="100"
        :allow-header-filtering="false"
      />
      <dx-column
        data-field="investmentsTypeId"
        data-type="number"
        :caption="$t('funding.list.type')"
        :editor-options="{ showClearButton: true }"
        :width="120"
      >
        <dx-lookup
          :data-source="investmentTypes"
          display-expr="investmentsType"
          value-expr="field_001"
        />
      </dx-column>
      <dx-column
        data-field="investmentsStage"
        data-type="number"
        :caption="$t('funding.list.stage')"
        :editor-options="{ showClearButton: true }"
        :width="120"
      >
        <dx-lookup
          :data-source="investmentStages"
          display-expr="investmentsStage"
          value-expr="field_001"
        />
      </dx-column>
      <dx-column
        data-field="statusId"
        data-type="number"
        :caption="$t('funding.list.status')"
        :editor-options="{ showClearButton: true }"
        :width="120"
      >
        <dx-lookup
          :data-source="profileStatus"
          display-expr="status"
          value-expr="field_001"
        />
      </dx-column>
      <dx-column
        v-if="checkAdmin(currentUser.mitarbeiterId)"
        :caption="$t('funding.list.totalFunding')"
        :calculate-cell-value="calcTotal"
        data-field="profile.cRMFundingProfileV2s"
        :format="totalFormat"
        :allow-editing="false"
        :allow-filtering="false"
        :allow-sorting="false"
        :allow-header-filtering="false"
        :width="120"
      />
      <dx-column
        data-field="profile.cRMAddress.technologyId"
        data-type="number"
        :caption="$t('funding.list.technology')"
        :editor-options="{ showClearButton: true }"
        :width="120"
      >
        <dx-lookup
          :data-source="brancheTypes"
          display-expr="brancheEn"
          value-expr="field_001"
        />
      </dx-column>
      <dx-column
        :caption="$t('funding.list.sectors')"
        data-field="profile.pROFILProfilJOINCategories"
        cell-template="sectors-template"
        :calculate-cell-value="calculateSectors"
        :width="400"
        :allow-editing="false"
        :editor-options="{ showClearButton: true }"
        :allow-filtering="false"
        :allow-sorting="false"
        :allow-header-filtering="false"
      >
        <dx-header-filter :data-source="sectorsHeaderFilter"/>
      </dx-column>
      <dx-column
        data-field="cRMFundingJOINProfilProfileV2s"
        cell-template="investors-template"
        :calculate-cell-value="calculateInvestors"
        :caption="$t('funding.list.investors')"
        :width="200"
        :allow-editing="false"
        :editor-options="{ showClearButton: true }"
        :allow-filtering="false"
        :allow-sorting="false"
        :allow-header-filtering="false"
      >
        <dx-header-filter :data-source="investorsHeaderFilter"/>
      </dx-column>
      <dx-column
        data-field="privacyJoin"
        data-type="number"
        :caption="$t('funding.list.publicRound')"
        :editor-options="{ showClearButton: true }"
        :width="120"
      >
        <dx-lookup
          :data-source="publicRoundValues"
          display-expr="displayValue"
          value-expr="key"
          :allow-clearing="true"
        />
      </dx-column>
      <dx-column
        :caption="$t('funding.list.awards')"
        data-field="profile.cRMAddress.cONTESTWinners"
        cell-template="awards-template"
        :calculate-cell-value="calculateAwards"
        :width="400"
        :editor-options="{ showClearButton: true }"
        :allow-editing="false"
        :allow-filtering="false"
        :allow-sorting="false"
        :allow-header-filtering="false"
      >
        <dx-header-filter :data-source="awardsHeaderFilter"/>
      </dx-column>
      <dx-column
        caption=""
        cell-template="button-template"
        type="buttons"
        :showInColumnChooser="false"
        :fixed="true"
        :width="80"
      />
      <template #sectors-template="{ data }">
        <div v-if="data.data.profile && data.data.profile.pROFILProfilJOINCategories.length">
          <span
            v-for="category in data.data.profile.pROFILProfilJOINCategories"
            :key="category.kategorie_idId"
          >
            <span v-if="category.pROFILCategory">{{category.pROFILCategory.kategorieDE}}, </span>
            <span v-else></span>
          </span>
        </div>
        <div v-else>
          <span></span>
        </div>
      </template>
      <template #investors-template="{ data }">
        <div>
          <span
            v-for="investor in data.data.cRMFundingJOINProfilProfileV2s"
            :key="investor.field_001"
          >
            <span v-if="investor.profil_idId">{{investor.profile.cRMAddress.company}}<br></span>
          </span>
        </div>
      </template>
      <template #awards-template="{ data }">
        <div v-if="data.data.profile && data.data.profile.cRMAddress && data.data.profile.cRMAddress.cONTESTWinners">
          <span
            v-for="award in data.data.profile.cRMAddress.cONTESTWinners"
            :key="award.field_001"
          >
            <span v-if="award.cONTESTExecution && award.cONTESTExecution.cONTESTContest.category_idId === 1">{{award.cONTESTExecution.contest_release}}, </span>
            <span v-else></span>
          </span>
        </div>
        <div v-else>
          <span></span>
        </div>
      </template>
      <template #button-template="{ data }">
        <a v-if="data.data.profile" :href="`https://crm.ifj.ch/index.cfm?&page=137659&crm_adresse_id=${data.data.profile.cRM_Adress_id}&ShowTab=FundingTab#detailTabsAnchor`" target="_blank"><i class="fas fa-pen mr-3"></i></a>
      </template>
      <dx-master-detail
        :enabled="true"
        template="master-detail"
      />
      <template #master-detail="{ data }">
        <funding-master-detail v-if="data" :masterDetailData="data"/>
      </template>
      <template #totalGroupCount>
        <div class="informer" v-if="initTotalCount != 0">
          <span class="count">{{ totalDevExpress }} results out of {{ initTotalCount }} records</span>
        </div>
      </template>
      <dx-toolbar>
        <dx-item
          location="before"
          template="totalGroupCount"
        />
        <dx-item
          name="searchPanel"
        />
        <dx-item
          location="after"
          template="actionmenu"
        />
      </dx-toolbar>
      <template #actionmenu>
        <div>
          <v-menu
            offset-y
            bottom
            left
            content-class="toolbar-action-menu"
          >
            <template v-slot:activator="{ attrs, on }">
              <v-btn
                color="secondary"
                v-bind="attrs"
                v-on="on"
              >
                <i class="fas fa-cog"></i> Actions
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                link
                @click="openColumnChooser"
              >
                <v-list-item-title><i class="fas fa-columns mr-1"></i> Column Chooser</v-list-item-title>
              </v-list-item>
              <v-tooltip left max-width="400" open-delay="500">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-list-item
                      link
                      @click="onExporting"
                      :disabled="!enableExport"
                    >
                      <v-list-item-title><i class="fas fa-file-excel mr-2"></i> Excel Export</v-list-item-title>
                    </v-list-item>
                  </span>
                </template>
                <span>For performance and security reasons, there is currently an export limit of 5000.<br>Large lists can take a few minutes to export</span>
              </v-tooltip>
              <v-list-item
                link
                @click="loadFundingModal"
              >
                <v-list-item-title><i class="fas fa-plus mr-2"></i> Add Fundings</v-list-item-title>
              </v-list-item>
              <v-tooltip left max-width="400" open-delay="500">
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item
                    link
                    @click="saveState"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-list-item-title><i class="fas fa-save mr-2"></i> Save Template</v-list-item-title>
                  </v-list-item>
                </template>
                <span>Save the columns template</span>
              </v-tooltip>
              <v-tooltip left max-width="400" open-delay="500">
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item
                    link
                    @click="loadState"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-list-item-title><i class="fas fa-download mr-2"></i> Load Template</v-list-item-title>
                  </v-list-item>
                </template>
                <span>Open a columns template</span>
              </v-tooltip>
            </v-list>
          </v-menu>
        </div>
      </template>
    </dx-data-grid>
    <save-template :template="template"/>
    <load-template :tableType="tableType" @set-state-3="loadSelectedTemplate" @set-default-state-3="loadDefaultTemplate"/>
    <add-fundings ref="myAddFundings"/>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import FundingMasterDetail from '@/components/fundings/MasterDetail.vue'
  import SaveTemplate from '@/components/template/SaveTemplate.vue'
  import LoadTemplate from '@/components/template/LoadTemplate.vue'
  import AddFundings from '@/components/fundings/AddFundings.vue'
  // import DxButton from 'devextreme-vue/button'
  import { exportDataGrid } from 'devextreme/excel_exporter'
  import ExcelJS from 'exceljs'
  import saveAs from 'file-saver'
  import { createCustomStore } from '@/store/helpers/remoteOperation.js'
  import { errorHandler } from '@/helpers/formHelper.js'
  import { isAdmin } from '@/helpers/auth.js'
  import _ from 'lodash'
  import moment from 'moment'
  import {
    DxDataGrid,
    DxColumn,
    DxScrolling,
    DxSearchPanel,
    DxColumnChooser,
    DxColumnChooserSearch,
    DxHeaderFilter,
    DxFilterRow,
    DxFilterPanel,
    DxMasterDetail,
    DxEditing,
    DxLookup,
    DxEmailRule,
    DxPaging,
    DxToolbar,
    DxItem
    // DxStateStoring
  } from 'devextreme-vue/data-grid'

  const dataGridFundings = 'dataGrid'

  export default {
    components: {
      DxDataGrid,
      DxColumn,
      DxScrolling,
      DxSearchPanel,
      DxColumnChooser,
      DxColumnChooserSearch,
      DxHeaderFilter,
      DxFilterRow,
      DxFilterPanel,
      DxMasterDetail,
      FundingMasterDetail,
      SaveTemplate,
      LoadTemplate,
      // DxButton,
      AddFundings,
      DxEditing,
      DxLookup,
      DxEmailRule,
      DxPaging,
      DxToolbar,
      DxItem
      // DxStateStoring
    },
    data () {
      return {
        hasError: false,
        errorMessage: '',
        loadingShow: true,
        dataIsLoaded: false,
        totalFormat: { style: 'currency', currency: 'CHF', useGrouping: true, minimumSignificantDigits: 3 },
        totalCount: 0,
        totalDevExpress: 0,
        initTotalCount: 0,
        dataGridFundings,
        template: {},
        tableType: 3,
        defaultState: null,
        sectorsHeaderFilter: [],
        investorsHeaderFilter: [],
        awardsHeaderFilter: [],
        publicRoundValues: [{ key: '2', displayValue: '' }, { key: '1', displayValue: 'Yes' }],
        pageSize: 50,
        currentEditingRowData: null,
        enableExport: false
      }
    },
    created () {
      this.getBasicValues('basicValues/GET_BRANCHE_TYPES', this.brancheTypes)
      this.getBasicValues('basicValues/GET_INVESTMENT_TYPES', this.investmentTypes)
      this.getBasicValues('basicValues/GET_INVESTMENT_STAGES', this.investmentStages)
      this.getBasicValues('basicValues/GET_PROFILE_STATUS', this.profileStatus)
      this.getBasicValues('basicValues/GET_CANTON_TYPES', this.cantonTypes)
      this.getBasicValues('basicValues/GET_SALUTATION_TYPES', this.salutationTypes)
      this.initStore()
    },
    mounted () {
      this.defaultState = JSON.stringify(this.dataGrid.state())
    },
    methods: {
      initStore () {
        const key = 'field_001'
        this.dataSource = createCustomStore({ key, self: this, methodName: 'getFundings', root: this.$root })
      },
      onEditingStart (e) {
        this.currentEditingRowData = e
        this.editRowKey = e.column.dataField
      },
      async onUpdateRow (rowData) {
        const mergedData = _.merge(this.currentEditingRowData, rowData)
        rowData = mergedData
        const editedQuery = this.editRowKey.split('.')
        const isAddressUpdate = editedQuery.includes('cRMAddress')
        const isFundingProfileUpdate = editedQuery.includes('investmentsTypeId') ||
          editedQuery.includes('statusId') || editedQuery.includes('moneyinCHF') ||
          editedQuery.includes('investmentsStage') || editedQuery.includes('closingdate') || editedQuery.includes('privacyJoin')

        if (isAddressUpdate) {
          if (rowData.data.profile.cRMAddress.technologyId === null) {
            rowData.data.profile.cRMAddress.technologyId = -1
          }
          rowData.data.profile.cRMAddress.salutationId = parseInt(rowData.data.profile.cRMAddress.salutationId)
          if (rowData.data.profile.cRMAddress.kantonId === null) {
            rowData.data.profile.cRMAddress.kantonId = -1
          }
          const { profile } = rowData.data
          this.onUpdateAddress(profile.cRMAddress)
          if (rowData.data.profile.cRMAddress.technologyId === -1) {
            rowData.data.profile.cRMAddress.technologyId = null
          }
          if (rowData.data.profile.cRMAddress.kantonId === -1) {
            rowData.data.profile.cRMAddress.kantonId = null
          }
        }
        if (isFundingProfileUpdate) {
          if (rowData.data.investmentsTypeId === null) {
            rowData.data.investmentsTypeId = -1
          }
          if (rowData.data.investmentsStage === null) {
            rowData.data.investmentsStage = -1
          }
          if (rowData.data.statusId === null) {
            rowData.data.statusId = -1
          }
          if (rowData.data.moneyinCHF === null) {
            rowData.data.moneyinCHF = 0
          }
          if (rowData.data.privacyJoin === null) {
            rowData.data.privacyJoin = '2'
          }
          this.onUpdateFundingProfile(rowData.data)
          if (rowData.data.investmentsTypeId === -1) {
            rowData.data.investmentsTypeId = null
          }
          if (rowData.data.investmentsStage === -1) {
            rowData.data.investmentsStage = null
          }
          if (rowData.data.statusId === -1) {
            rowData.data.statusId = null
          }
        }
      },
      async onUpdateAddress (data) {
        try {
          const { errors } = await this.$store.dispatch('fundings/UPDATE_ADDRESS', {
            formData: data
          })
          if (!errors) {
            this.hasError = false
          } else {
            throw errors
          }
        } catch (err) {
          this.hasError = true
          this.errorMessage = this.$t('formError.unknownwError')
          setTimeout(() => {
            this.hasError = false
          }, 5000)
          console.error(err)
        }
      },
      async onUpdateFundingProfile (data) {
        try {
          const { errors } = await this.$store.dispatch('fundings/UPDATE_FUNDING_PROFILE', {
            formData: data
          })
          if (!errors) {
            this.hasError = false
          } else {
            throw errors
          }
        } catch (err) {
          this.hasError = true
          this.errorMessage = this.$t('formError.unknownwError')
          setTimeout(() => {
            this.hasError = false
          }, 5000)
          console.error(err)
        }
      },
      loadFundingModal () {
        // window.open('https://crm.ifj.ch/index.cfm?&page=137762', '_blank').focus()
        this.$refs.myAddFundings.resetForm()
        this.$store.commit('fundings/SET_ADD_FUNDINGMODAL_STATUS', true)
      },
      contentReady () {
        this.totalDevExpress = this.dataGrid.totalCount()
        if (this.initTotalCount === 0) {
          this.initTotalCount = this.dataGrid.totalCount()
        }
        // exporting is only enabled if resuts are less than 8'000, because of performance reasons
        // const excelExporter = document.getElementsByClassName('dx-datagrid-export-button')
        this.enableExport = this.totalDevExpress <= 5000
      },
      loadDefaultTemplate () {
        const state = JSON.parse(this.defaultState)
        this.dataGrid.state(state)
      },
      loadSelectedTemplate (state) {
        state = JSON.parse(state)
        this.dataGrid.state(state)
      },
      loadState () {
        this.$store.commit('template/SET_LOAD_TEMPLATE_STATUS', true)
      },
      saveState () {
        this.template = {
          state: JSON.stringify(this.dataGrid.state()),
          table_type: this.tableType,
          creator: this.currentUser.mitarbeiterId
        }
        this.$store.commit('template/SET_SAVE_TEMPLATE_STATUS', true)
      },
      async getFundings (gqlOptions) {
        this.hasError = false
        this.$Progress.start()

        try {
          const data = await this.$store.dispatch('fundings/GET_FUNDINGS_PAGINATION', {
            gqlOptions
          })

          const { items, totalCount } = data.data.response

          this.totalCount = totalCount
          this.loadingShow = false
          this.dataIsLoaded = true
          this.$Progress.finish()

          return {
            data: items,
            totalCount
          }
        } catch (err) {
          errorHandler(this, err)
          console.error(err)
        }
      },
      getDisplayExpr (item) {
        return item.geschlecht_PopupExtension ? `${item.ansprechzeile} ${item.geschlecht_PopupExtension}` : `${item.ansprechzeile}`
      },
      openRowExpanding (e) {
        e.component.collapseAll(-1)
        if (!e.component.getSelectedRowKeys().length) { e.component.selectRowsByIndexes(0) }
      },
      publicRoundCellValue (rowData) {
        return rowData.privacyJoin === '1' ? 'Yes' : null
      },
      calculateSectors (rowData) {
        let listOfSectors = ''
        if (rowData.profile && rowData.profile.pROFILProfilJOINCategories.length) {
          listOfSectors = Array.prototype.map.call(rowData.profile.pROFILProfilJOINCategories, function (item) { if (item.pROFILCategory) { return item.pROFILCategory.kategorieDE } else { return '' } }).join(', ')
        }
        return listOfSectors
      },
      calculateInvestors (rowData) {
        let listOfInvestors = ''
        if (rowData.cRMFundingJOINProfilProfileV2s) {
          listOfInvestors = Array.prototype.map.call(rowData.cRMFundingJOINProfilProfileV2s, function (item) { if (item.profil_idId) { return item.profile.cRMAddress.company } else { return '' } }).join(', ')
        }
        return listOfInvestors
      },
      calculateAwards (rowData) {
        let listOfAwards = ''
        if (rowData.profile && rowData.profile.cRMAddress && rowData.profile.cRMAddress.cONTESTWinners) {
          listOfAwards = Array.prototype.map.call(rowData.profile.cRMAddress.cONTESTWinners, function (item) { if (item.cONTESTExecution && item.cONTESTExecution.cONTESTContest.category_idId === 1) { return item.cONTESTExecution.contest_release } else { return '' } }).join(', ')
        }
        return listOfAwards
      },
      calcTotal (rowData) {
        let total = 0
        if (rowData.profile) {
          // statusId 2 = Closed, InvestmentStage 10 = Trade Sale, InvestmentType 9 = Loan, 15 = Revenue
          if (rowData.profile.cRMFundingProfileV2s) {
            rowData.profile.cRMFundingProfileV2s.forEach(fund => {
              if (fund.statusId === 2 && fund.investmentsStage !== 10 &&
                ![9, 15].includes(fund.investmentsTypeId)) { (total += fund.moneyinCHF) }
            })
          }
        }
        return total
      },
      onExporting () {
        this.$store.commit('fundings/SET_EXPORT_TOTAL_COUNT', null)
        this.$store.commit('fundings/SET_EXPORT_TOTAL_COUNT', this.totalCount)
        const workbook = new ExcelJS.Workbook()
        const worksheet = workbook.addWorksheet('Fundings')
        setTimeout(() => {
          exportDataGrid({
            component: this.dataGrid,
            worksheet: worksheet,
            autoFilterEnabled: true
          }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
              saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${moment(this.currentDate).format('DD.MM.YYYY')}-Fundings.xlsx`)
            })
          })
          // this.dataGrid.cancel = true
        }, 1000)
      },
      async getBasicValues (storePath, storeValue) {
        if (storeValue.length === 0) {
          try {
            const { errors } = await this.$store.dispatch(storePath)
            if (errors) throw errors
          } catch (err) {
            this.hasError = true
            this.errorMessage = this.$t('formError.unknownwError')
            setTimeout(() => {
              this.hasError = false
            }, 5000)
            console.error(err)
          }
        }
      },
      openColumnChooser () {
        this.dataGrid.showColumnChooser()
      },
      checkAdmin (id) {
        return isAdmin(id)
      }
    },
    computed: {
      /* casequalityCustomSource () {
        const customStore = this.casequalityTypes.map(({
          casequality: text,
          field_001: value,
          ...rest
        }) => ({
          text,
          value,
          ...rest
        }))
        return customStore
      }, */
      ...mapGetters({
        items: 'fundings/FUNDINGS',
        brancheTypes: 'basicValues/BRANCHE_TYPES',
        investmentTypes: 'basicValues/INVESTMENT_TYPES',
        investmentStages: 'basicValues/INVESTMENT_STAGES',
        profileStatus: 'basicValues/PROFILE_STATUS',
        cantonTypes: 'basicValues/CANTON_TYPES',
        salutationTypes: 'basicValues/SALUTATION_TYPES'
      }),
      ...mapState({
        currentUser: state => state.user.currentUser
      }),
      dataGrid: function () {
        return this.$refs[dataGridFundings].instance
      }
    }
  }
</script>
<style lang="scss">
#gridContainerFundings {
    height: 78vh;
    margin-bottom: 0;
}

.btn-pdl {
  margin: 0 0 0 5px;
}

.dx-datagrid-export-button {
  &.disable-exporting {
    pointer-events: none;

    .dx-icon-xlsxfile {
      color: lightgray;
    }
  }
}

</style>
